/**
 * 作り直しの書込 1 回分の適用（API §3.0・§3.1）— 採番 → 置き換え → 検査 → 2 表への書き分け。
 *
 * 書込の経路（マスター / 用語 / 処理 / 業務 …）が違うのは「どんな欄を受けるか」だけで、
 * そこから先は同じである。番号を採り、欄の中の仮の番号を置き換え、契約の入力スキーマに通し、
 * 対象の型と参照先の型を見て、F7a の書き分け（案件の変更は消して入れる / 操作履歴は足す）に
 * 渡す。経路ごとにこの手順を書き写すと、置き換えを忘れた経路や、型の検査を飛ばす経路が生まれる。
 *
 * **欠番は出る。** 形式検査で落ちる入力にも番号を先に採る（置き換えてからでないと契約の
 * スキーマに通せない）。設計書の判断どおり、欠番の害は無い — 埋めようとするとカウンタを戻す
 * 経路が要り、同時に走る別の書込と番号が衝突する（`id-allocation.ts`）。
 *
 * **master を読む口は注入する。** 新しい行スキーマの master は切替（C1）で表を作り直すまで
 * 存在しない。ここに「表が無ければ空」を書くと、切替のあとも空を返し続ける経路が残る。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの書込 1 回分を適用する（採番・仮の番号の置き換え・形式検査・対象と参照先の型の検査・案件の変更と操作履歴への書き分け）
 * @graph-connects none
 */
import { z } from 'zod';
import { readLocalizedText } from '../../contract/locales.js';
import { planOperationRows } from './operation-log.js';
import { composeElementSnapshot, planPjChanges, } from './pj-changes.js';
import { collectElementReferences, describeExpectedReference, substituteElementReferences, } from './references.js';
/**
 * 書込を止める理由。**ここに無い理由は出さない。**
 *
 * 設計書の拒否は 6 種（形式検査 / 対象不在 / 名前の重複 / 土台の食い違い / 確定済み /
 * 参照先の型）だが、**名前の重複と土台の食い違いはこの経路では出ない**。前者は名前の作りの
 * 一意性、後者は `base_revision` の突合で、どちらも master のノードの姿と更新時刻を要する。
 * 新しい行スキーマの master は切替（C1）まで存在しないので、出せない理由を語彙に置かない。
 *
 * 逆に**対象の型の食い違いは設計書の 6 種に無いが要る**。番号に型が畳まれなくなった新しい契約
 * （決定 1）では、書いている要素の型は呼び出し側の申告でしかない。既にあるノードの実際の型と
 * 突き合わせないと、型ごとに違う関門（マスターの管理者のロール・承認が要る型）を別の型を名乗って
 * 迂回でき、承認者には「用語の変更」に見える行が実は業務の消滅である、という状態を作れる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの書込が返す拒否理由 5 種の閉集合
 * @graph-connects none
 */
export const ELEMENT_WRITE_REJECTION_REASONS = [
    'input-schema',
    'target-not-found',
    'target-type-mismatch',
    'reference-type-mismatch',
    'pj-already-merged',
];
/**
 * 拒否 1 件だけの結果を組む。経路の入口（封筒の形式検査・認可）から呼ぶ。
 *
 * @param reason 止めた理由
 * @param pointer どこで止めたか
 * @param message 何が起きているか
 * @returns 拒否 1 件の結果
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 拒否 1 件だけの書込結果を組む
 * @graph-connects none
 */
export function rejectElementWrite(reason, pointer, message) {
    return { outcome: 'rejected', rejections: [{ reason, pointer, message }] };
}
/**
 * zod の検査結果を拒否の一覧へ直す。**1 件も畳まない。**
 *
 * 形式検査は 1 回の呼び出しで複数の欄に落ちうる。先頭だけ返すと、直して送り直すたびに次の 1 件が
 * 出てくる往復になる。
 *
 * @param pointer 入力のどこを検査したか（封筒なら空文字）
 * @param error zod の検査結果
 * @returns 拒否の一覧。欄の場所は `pointer` から続けて組む
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business zod の形式検査の結果を、欄の場所つきの拒否の一覧へ直す
 * @graph-connects none
 */
export function toInputSchemaRejections(pointer, error) {
    return error.issues.map((issue) => ({
        reason: 'input-schema',
        pointer: [pointer, ...issue.path.map((segment) => String(segment))].filter(Boolean).join('.'),
        message: issue.message,
    }));
}
/**
 * 書込が要求を指す欄。**必須である**（決定 3。変更行の `reason` は無くなり、なぜ変えたかは
 * 要求と why の層が持つ）。
 *
 * 受けるのは既にある要求の番号だけである。新しい要求をここで立てる形（`statement` /
 * `acceptance` / `whys[]`）は、要求の層の id が number になってから入る。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 書込が実現する要求を番号で指す欄の入力スキーマ
 * @graph-connects none
 */
export const elementRequirementSchema = z.strictObject({
    requirementId: z.number().int().positive(),
});
/**
 * 書込の対象を指す番号。**0 は受けない。**
 *
 * 正の数は既にある要素、負の数は同じ呼び出しの中で作る要素を指す仮の番号である（API §3.0）。
 * 0 はどちらでもないので、`id` を数えて埋めた（`index` を渡した）入力をここで止める。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 書込の対象を指す番号（既存は正・同じ呼び出しで作るものは負）の入力スキーマ
 * @graph-connects none
 */
export const elementWriteTargetIdSchema = z
    .number()
    .int()
    .refine((value) => value !== 0, {
    message: '0 は要素を指さない（既にある要素は正の番号、同じ呼び出しで作るものは負の番号）',
});
/**
 * 参照先が求める姿と合っているかを見る。用語だけ区画まで見る。
 * @graph-connects none
 */
function matchesExpected(value, expect) {
    if (value.nodeType !== expect.nodeType)
        return false;
    if (expect.nodeType !== 'Term')
        return true;
    return value.properties['section'] === expect.section;
}
/**
 * 新規のぶんだけ番号を採り、入力の並びに沿って割り当てる。
 *
 * 既にある要素を指した入力（正の `id` を渡した upsert と delete）には番号を採らない。採ると、
 * 直すだけの書込でカウンタが進む。
 *
 * @param items 書く 1 件以上
 * @param allocateIds 連番の採番
 * @returns 入力と同じ並びの「1 件と番号」、仮の番号の対応表、応答に返す採番の一覧
 * @graph-connects none
 */
async function allocateNodeIds(items, allocateIds) {
    const slots = items.map((item) => {
        if (item.operation === 'delete')
            return { item, nodeId: item.id };
        const given = item.id;
        if (given !== undefined && given > 0)
            return { item, nodeId: given };
        return { item, provisional: given ?? null };
    });
    const newCount = slots.filter((slot) => !('nodeId' in slot)).length;
    let next = newCount === 0 ? 0 : (await allocateIds('nodes', newCount)).from;
    const numbered = [];
    const idMap = new Map();
    const allocations = [];
    for (const slot of slots) {
        if ('nodeId' in slot) {
            numbered.push(slot);
            continue;
        }
        const nodeId = next;
        next += 1;
        numbered.push({ item: slot.item, nodeId });
        allocations.push({ provisional: slot.provisional, id: nodeId });
        if (slot.provisional !== null)
            idMap.set(slot.provisional, nodeId);
    }
    return { numbered, idMap, allocations };
}
/**
 * 仮の番号を置き換え、契約の入力スキーマに通す。
 * @graph-connects none
 */
function resolveItems(numbered, idMap) {
    const entries = [];
    const rejections = [];
    for (const [index, { item, nodeId }] of numbered.entries()) {
        const pointer = `operations.${String(index)}`;
        const existing = item.operation === 'delete' || (item.id !== undefined && item.id > 0);
        if (item.operation === 'delete') {
            entries.push({
                pointer,
                nodeId,
                nodeType: item.nodeType,
                requirementId: item.requirementId,
                existing,
                after: null,
                references: [],
                preservedFields: [],
            });
            continue;
        }
        const substituted = substituteElementReferences(item.payload, item.spec.references, idMap);
        if (substituted.unresolved.length > 0) {
            // 置き換えられなかった負の数を契約の入力スキーマへ流すと、「正の整数である」という
            // 一般の文言が同じ欄にもう 1 件並ぶ。原因を名指している側だけを返す。
            for (const gap of substituted.unresolved) {
                rejections.push({
                    reason: 'input-schema',
                    pointer: `${pointer}.${gap.pointer}`,
                    message: `仮の番号 ${String(gap.id)} を指しているが、この呼び出しの中でその番号の要素を作っていない`,
                });
            }
            continue;
        }
        const body = substituted.payload;
        const candidate = body === null || typeof body !== 'object' || Array.isArray(body)
            ? body
            : { ...body, id: nodeId };
        const parsed = item.spec.schema.safeParse(candidate);
        if (!parsed.success) {
            rejections.push(...toInputSchemaRejections(pointer, parsed.error));
            continue;
        }
        const after = parsed.data;
        entries.push({
            pointer,
            nodeId,
            nodeType: item.nodeType,
            requirementId: item.requirementId,
            existing,
            after,
            references: collectElementReferences(after, item.spec.references),
            preservedFields: item.preservedFields ?? [],
        });
    }
    return { entries, rejections };
}
/**
 * 引き継ぐ欄を、今の値（master ⊕ 案件の姿）から書いたあとの値へ写す。
 *
 * 今の値に無い欄は写さない（書いたあとの値にも無いまま）。新規のノードは今の値が無いので
 * 何もしない。
 * @graph-connects none
 */
function carryPreservedFields(entries, applied) {
    return entries.map((entry) => {
        if (entry.after === null || entry.preservedFields.length === 0)
            return entry;
        const current = applied.get(entry.nodeId);
        if (current === undefined)
            return entry;
        const carried = { ...entry.after };
        for (const field of entry.preservedFields) {
            if (field in current.properties)
                carried[field] = current.properties[field];
        }
        return { ...entry, after: carried };
    });
}
/**
 * 同じノードを 2 度書いている箇所を探す。
 * @graph-connects none
 */
function findDuplicateNodeId(entries) {
    const seen = new Set();
    for (const entry of entries) {
        if (seen.has(entry.nodeId))
            return { pointer: entry.pointer, nodeId: entry.nodeId };
        seen.add(entry.nodeId);
    }
    return null;
}
/**
 * 対象の実在と型、参照先の型を見て、あわせて番号をラベルへ解決する。
 *
 * **対象の型は必ず突き合わせる。** 新しい契約では番号に型が畳まれていないので、書いている型は
 * 呼び出し側の申告でしかない。実在だけを見ると、管理者のロールが要らない型（ステージ）を名乗って動詞・単位を
 * 上書きしたり、誰でも打てる用語の一括で業務・処理・画面を用語に化けさせたりできる。**対象の
 * 実型と申告が違えば書かない。** 型を変えるのは「消してから作り直す」であって、直すではない。
 *
 * **参照先が見えないことは拒否にしない。** 実在は制約 4（リリースゲート）が見る。この段では
 * master が読めない参照もあるので、見えない相手を違反にすると正しい参照まで止まる。代わりに
 * 解決の一覧へ「ラベル null」で載せ、書いた人が読んで確かめられるようにする。
 * @graph-connects none
 */
function checkTargetsAndReferences(entries, applied, visible) {
    const rejections = [];
    const resolved = [];
    for (const entry of entries) {
        const current = entry.existing ? applied.get(entry.nodeId) : undefined;
        if (entry.existing && current === undefined) {
            rejections.push({
                reason: 'target-not-found',
                pointer: `${entry.pointer}.id`,
                message: `ノード ${String(entry.nodeId)} はこの案件から見えない（新規なら id を省く）`,
            });
        }
        else if (current !== undefined && current.nodeType !== entry.nodeType) {
            rejections.push({
                reason: 'target-type-mismatch',
                pointer: `${entry.pointer}.id`,
                message: `ノード ${String(entry.nodeId)} は ${current.nodeType} で、${entry.nodeType} として書き直す・消すことはできない`,
            });
        }
        for (const reference of entry.references) {
            const target = visible.get(reference.id);
            resolved.push({
                pointer: `${entry.pointer}.${reference.pointer}`,
                id: reference.id,
                label: target === undefined ? null : readLocalizedText(target.properties['label']),
            });
            if (target === undefined || matchesExpected(target, reference.expect))
                continue;
            rejections.push({
                reason: 'reference-type-mismatch',
                pointer: `${entry.pointer}.${reference.pointer}`,
                message: `${describeExpectedReference(reference.expect)}を指す欄に、${target.nodeType} のノード ${String(reference.id)} が書かれている`,
            });
        }
    }
    return { rejections, resolved };
}
/**
 * 書込 1 件の並びを、要求ごとの計画へまとめる。
 *
 * `ElementWritePlan` は要求を 1 つ持つ（F7a）。1 回の呼び出しで別々の要求に属する要素を書け
 * るので、要求ごとに計画を分ける。並びは要求が最初に現れた順で、同じ要求の中は入力の順である。
 * @graph-connects none
 */
function groupIntoPlans(pjNumber, entries, applied) {
    const byRequirement = new Map();
    for (const entry of entries) {
        const change = {
            nodeId: entry.nodeId,
            nodeType: entry.nodeType,
            before: applied.get(entry.nodeId)?.properties ?? null,
            after: entry.after,
        };
        const changes = byRequirement.get(entry.requirementId);
        if (changes === undefined) {
            byRequirement.set(entry.requirementId, [change]);
            continue;
        }
        changes.push(change);
    }
    return [...byRequirement].map(([requirementId, changes]) => ({
        pjNumber,
        requirementId,
        changes,
    }));
}
/**
 * 書込 1 回分を適用する。
 *
 * 手順は 6 つで、どれか 1 つでも拒否なら**1 行も書かない**（途中まで書いて残りを拒否すると、
 * 呼び出し側が「どこまで入ったか」を知る手段を持たない）。
 *
 * 1. 新規のぶんだけ番号を採る
 * 2. 欄の中の仮の番号を採番した id へ置き換え、契約の入力スキーマに通す
 * 3. 同じノードを 2 度書いていないかを見る
 * 4. master ⊕ 案件の姿を読み、引き継ぐ欄を今の値から写し、対象の実在と型、参照先の型を見る
 * 5. 要求ごとに計画を組む（`ElementWritePlan` は要求を 1 つ持つ）
 * 6. 案件の変更を消して入れ、操作履歴を足す（`dry_run` なら置き場に触らない）
 *
 * @param args 案件番号・検査だけか・書込主体・書込の種別・書く 1 件以上・依存
 * @returns 適用 / 検査だけ / 拒否
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの書込 1 回分を、採番から案件の変更と操作履歴への書き分けまで通す
 * @graph-connects none
 */
export async function applyElementWrite(args) {
    const allocated = await allocateNodeIds(args.items, args.deps.allocateIds);
    const resolved = resolveItems(allocated.numbered, allocated.idMap);
    if (resolved.rejections.length > 0) {
        return { outcome: 'rejected', rejections: resolved.rejections };
    }
    const duplicate = findDuplicateNodeId(resolved.entries);
    if (duplicate !== null) {
        return rejectElementWrite('input-schema', duplicate.pointer, `同じノード ${String(duplicate.nodeId)} をこの呼び出しの中で 2 度書いている（案件の変更は（案件, ノード）で 1 行なので 1 件にまとめる）`);
    }
    const lookupIds = new Set();
    for (const entry of resolved.entries) {
        lookupIds.add(entry.nodeId);
        for (const reference of entry.references)
            lookupIds.add(reference.id);
    }
    const master = await args.deps.readMaster([...lookupIds]);
    const current = await args.deps.store.listPjChanges(args.pjNumber);
    const applied = composeElementSnapshot(master, current, () => []).nodes;
    const entries = carryPreservedFields(resolved.entries, applied);
    const visible = new Map(applied);
    for (const entry of entries) {
        if (entry.after === null)
            continue;
        visible.set(entry.nodeId, { nodeType: entry.nodeType, properties: entry.after });
    }
    const checked = checkTargetsAndReferences(entries, applied, visible);
    if (checked.rejections.length > 0) {
        return { outcome: 'rejected', rejections: checked.rejections };
    }
    const at = args.deps.now().toISOString();
    const pjChanges = [];
    const operations = [];
    for (const plan of groupIntoPlans(args.pjNumber, entries, applied)) {
        const planned = planPjChanges({ plan, master, current, at, actor: args.actor });
        if (planned.outcome === 'rejected') {
            return rejectElementWrite('pj-already-merged', 'pj_number', `案件 ${String(args.pjNumber)} は確定済みで、これ以上書けない`);
        }
        pjChanges.push(...planned.rows);
        if (args.dryRun)
            continue;
        await args.deps.store.replacePjChanges(args.pjNumber, planned);
        const opIds = await args.deps.allocateIds('operations', plan.changes.length);
        const rows = planOperationRows({
            plan,
            writeKind: args.writeKind,
            opIds,
            at,
            actor: args.actor,
        });
        await args.deps.store.appendOperations(rows);
        operations.push(...rows);
    }
    return {
        outcome: args.dryRun ? 'checked' : 'applied',
        allocations: allocated.allocations,
        references: checked.resolved,
        pjChanges,
        operations,
    };
}
//# sourceMappingURL=write.js.map